const clone = value => JSON.parse(JSON.stringify(value));

export class DemoDataService {
  constructor() {
    this.mode = "demo";
    this.systemRole = "system_admin";
    this.profile = {
      id: "demo-admin",
      full_name: "NAYSOR Demo Admin",
      email: "demo@naysor.local",
      title: "System Administrator",
      avatar_url: "",
      language: localStorage.getItem("naysor-project-control-language") || "ko",
      timezone: "Asia/Seoul",
      system_role: "system_admin",
      active: true
    };
    this.workspaceId = "demo-workspace";
    this.listeners = new Set();
  }

  async initialize(seed) {
    this.state = clone(seed);
    this.state.meta = {
      ...this.state.meta,
      appVersion: "5.0.0-poc.1",
      storageMode: "DEMO MEMORY",
      lastSaved: ""
    };
    this.state.profiles = [this.profile];
    this.state.attachments = this.state.attachments || [];
    this.state.costs = this.state.costs || [];
    this.state.googleCalendarEventLinks = this.state.googleCalendarEventLinks || [];
    this.state.strategicGoals = this.state.strategicGoals || [];
    this.state.evidenceItems = this.state.evidenceItems || [];
    this.state.entityLinks = this.state.entityLinks || [];
    this.state.intelligencePredictions = this.state.intelligencePredictions || [];
    this.state.graphViews = this.state.graphViews || [];
    this.state.atlasNodePositions = this.state.atlasNodePositions || [];
    this.state.ideas = this.state.ideas || [];
    this.state.teaProjects = this.state.teaProjects || [];
    this.state.projectMembers = this.state.projects.map(project => ({
      id: "demo-member-" + project.id,
      projectId: project.id,
      profileId: this.profile.id,
      role: "project_manager",
      active: true
    }));
    return this.state;
  }

  async reload() {
    return this.state;
  }

  async persistEntity(type, record, previous = null) {
    const demoIdPrefixes = {strategic_goal:"demo-goal",evidence:"demo-evidence",entity_link:"demo-link",intelligence_prediction:"demo-prediction",graph_view:"demo-graph-view",atlas_node_position:"demo-atlas-pos",idea:"demo-idea",tea_project:"demo-tea-project"};
    const generatedId = record.id || (demoIdPrefixes[type] ? `${demoIdPrefixes[type]}-${Date.now()}-${Math.random().toString(36).slice(2,7)}` : record.id);
    const next = {
      ...record,
      id: generatedId,
      _dbId: record._dbId || generatedId,
      version: Number(previous?.version || record.version || 0) + 1,
      updatedAt: new Date().toISOString(),
      updatedBy: this.profile.id
    };
    this.emit({ table: type, eventType: previous ? "UPDATE" : "INSERT", new: next });
    return next;
  }

  async persistMany(type, records) {
    return Promise.all(records.map(record => this.persistEntity(type, record, record)));
  }

  async softDeleteEntity(type, record) {
    this.emit({ table: type, eventType: "DELETE", old: record });
    return true;
  }

  async updateGateChecklist(gate, checklistItem) {
    return this.persistEntity("gate_checklist", checklistItem, checklistItem);
  }

  async decideGate(gate, decision) {
    return this.persistEntity("gate", {...gate, ...decision}, gate);
  }

  async importSnapshot(snapshot) {
    this.state = clone(snapshot);
    this.state.profiles = [this.profile];
    this.state.projectMembers = [];
    this.emit({ table: "snapshot", eventType: "IMPORT" });
    return this.state;
  }

  async inviteUser() {
    throw new Error("Demo mode does not create production users. Configure Supabase first.");
  }

  async searchMarketEquipment() {
    // Demo runs entirely in memory with no server, so there is nothing to search
    // with. The old wording blamed a missing OPENAI_API_KEY, which sent people
    // hunting for a configuration problem that does not exist — the key is set;
    // demo simply has no backend. Say what is actually true and what to do.
    throw new Error("Demo 모드에는 서버 연결이 없어 Market 검색을 실행할 수 없습니다. 로그인해서 Cloud 세션으로 접속하면 실제 판매 장비를 검색합니다.");
  }

  async saveMarketReference() {
    throw new Error("Demo 모드에는 서버 연결이 없어 Market reference를 저장할 수 없습니다. 로그인 후 이용해 주세요.");
  }

  async listManagedUsers() {
    return { users: [this.profile], projects: this.state.projects || [] };
  }

  async updateUserAccess() { return true; }
  async deactivateUser() { throw new Error("Demo mode does not deactivate users."); }
  async reactivateUser() { throw new Error("Demo mode does not reactivate users."); }
  async resendInvitation() { throw new Error("Demo mode does not send invitation email."); }

  async updateProfile(changes) {
    Object.assign(this.profile, changes);
    return this.profile;
  }

  async updatePassword() {
    throw new Error("Demo mode does not manage passwords. Configure Supabase first.");
  }

  async generateAccessLink() {
    throw new Error("Demo mode does not generate access links. Configure Supabase first.");
  }

  async uploadAvatar() {
    throw new Error("Demo 모드에서는 이미지 업로드를 저장하지 않습니다. Supabase를 연결하세요.");
  }

  async createExternalAttachment(entityType, entityId, file) {
    const item = { id: `demo-attachment-${Date.now()}`, entityType, entityId, title: file.name || file.title, url: file.webViewLink || file.url || file.previewUrl, provider: file.provider || "link", externalId: file.id || "", mimeType: file.mimeType || "", previewUrl: file.previewUrl || "", webViewLink: file.webViewLink || file.url || "" };
    this.state.attachments.push(item);
    this.emit({ table: "attachments", eventType: "INSERT", new: item });
    return item;
  }

  async deleteExternalAttachment(id) {
    this.state.attachments = this.state.attachments.filter(item => item.id !== id);
    this.emit({ table: "attachments", eventType: "DELETE", old: { id } });
    return true;
  }

  async askNaysorAI() { throw new Error("AI_NOT_CONFIGURED"); }
  async analyzeEvidenceAttachment() { throw new Error("AI_FILE_ANALYSIS_DISABLED"); }

  subscribe(onChange, onPresence) {
    if (onChange) this.listeners.add(onChange);
    if (onPresence) onPresence([this.profile]);
    return () => {
      if (onChange) this.listeners.delete(onChange);
    };
  }

  setProjectScope() {}
  setEditing() {}

  emit(payload) {
    this.listeners.forEach(listener => listener(payload));
  }
}
